import { ButtonDropdown } from '@/components/common';
import { buildColumn, ColumnsProps } from '@/Helper';
import { getPermissionErrorMessage, SecuredComponents, User } from '@/model/AccountModel';
import { OptionsButtonBuilder } from '@/model/DeviceAdminModel';
import { CaretDownOutlined } from '@ant-design/icons';
import { Button, Input, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TableRowSelection } from 'antd/es/table/interface';
import React, { useState } from 'react';
import { FunctionGroupExtensionColumns } from '../FunctionGroupModal';
import styles from './functionExtensionView.module.scss';

const user: User = getUser();
const componentPermission = User.getComponentPermission(user, SecuredComponents.Function_Group_Extensions);

const createButtonOptions = (hasMoreThanOneKeySelected: boolean, hasNoneSelected: boolean): OptionsButtonBuilder<string> => {
	return {
		labelOrIcon: '...',
		options: [
			{
				id: 'functionGroupsTableActionDropdownEditButton',
				label: _('Edit'),
				disabled: !componentPermission.canView || hasMoreThanOneKeySelected,
				title: getPermissionErrorMessage(componentPermission.canUpdate, false, hasMoreThanOneKeySelected),
				value: 'edit',
			},
			{
				id: 'functionGroupsTableActionDropdownDeleteButton',
				label: _('Delete'),
				disabled: !componentPermission.canDelete || hasNoneSelected,
				title: getPermissionErrorMessage(componentPermission.canDelete),
				value: 'delete',
			},
		],
	};
};

type Props = {
	functionGroupExtensionData: FunctionGroupExtensionColumns[];
	functionGroupName: string;
	setFnGroupName: (newFnGroupName: string) => void;
	defineExtensionChange: (selectedKey?: number) => void;
	setRowKey: (key: string) => void;
	deleteCodeExtensions: (keys: string[]) => Promise<boolean>;
};

const FunctionExtensionView: React.FC<Props> = ({
	defineExtensionChange,
	setRowKey,
	setFnGroupName,
	functionGroupExtensionData,
	functionGroupName,
	deleteCodeExtensions,
}) => {
	//#region State Management Variables
	const [selectedRowKeys, setSelectedRowKey] = useState<React.Key[]>([]);
	const contextualMenuItemOptions = createButtonOptions(selectedRowKeys.length > 1, selectedRowKeys.length === 0);
	//#endregion

	const onChange = (keys: number[]) => {
		setSelectedRowKey(keys);
	};

	const handleActionScope = async (key: string) => {
		switch (key) {
			case 'delete': {
				const boolean = await deleteCodeExtensions(selectedRowKeys.map(key => key.toString()));
				setSelectedRowKey(boolean ? [] : selectedRowKeys);
				break;
			}
			case 'edit': {
				setRowKey(selectedRowKeys[0].toString());
				break;
			}
		}
	};

	const rowSelection: TableRowSelection<FunctionGroupExtensionColumns> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		getCheckboxProps: record => ({
			id: `functionGroupTableCheckbox-${record.key}`,
		}),
		selectedRowKeys,
		onChange,
	};

	const columns: ColumnsProps<FunctionGroupExtensionColumns>[] = [
		{
			...buildColumn(_('Extension'), 'Extension', 'auto', 'start'),
			sorter: (a, b) => a.Extension.localeCompare(b.Extension),
		},
		{
			...buildColumn(_('Function'), 'Name', 'auto', 'start'),
			sorter: false,
		},
		{
			...buildColumn(_('Detail'), 'Detail', 'auto', 'start'),
			sorter: false,
		},
	];

	return (
		<Spin tip={`${_('Loading')}...`} spinning={false} size='default'>
			<div className={styles.nameFunction}>
				<label className={styles.nameFunctionLabel}>
					{`${_('Name')}:`}
					<Input className={styles.controlName} maxLength={32} value={functionGroupName} onChange={e => setFnGroupName(e.target.value)} />
				</label>
			</div>
			<div className={styles.nameFunction}>
				<label className={styles.nameFunctionLabel}>
					{_('CodeExtensions')}:
					<div className={styles.controlName}>
						<ButtonDropdown
							id={'functionGroupsTableActionDropdown'}
							menuOptions={contextualMenuItemOptions.options}
							icon={<CaretDownOutlined />}
							labelIcon={contextualMenuItemOptions.labelOrIcon}
							onClickOption={handleActionScope}
							disabled={selectedRowKeys.length === 0}
						/>
					</div>
				</label>
			</div>
			<Table
				columns={columns as ColumnsType<FunctionGroupExtensionColumns>}
				rowSelection={rowSelection}
				dataSource={functionGroupExtensionData}
				className={styles.functionsTable}
				scroll={{ y: '250px' }}
				size='small'
				pagination={false}
			/>
			<div className={styles.defineExtension}>
				<Button
					id='DefineExtensionButton'
					type='primary'
					className={styles.defineExtensionButton}
					key='defineExtension'
					disabled={!componentPermission.canAdd}
					onClick={() => defineExtensionChange()}>
					{_('DefineNewExtension')}
				</Button>
			</div>
		</Spin>
	);
};

export { FunctionExtensionView };
